import { UserSubscription } from '@griply/types';
import { isAppleSubscription } from '@griply/utils';
import Typography from '@mui/material/Typography';
import { useOpenSubscriptionManagement } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';

import * as Styled from './delete-account.style';

export type DeleteAccountSubscriptionWarningProps = {
  subscription: UserSubscription;
  onCancel: () => void;
};

export const DeleteAccountSubscriptionWarning: React.FC<
  DeleteAccountSubscriptionWarningProps
> = ({ subscription, onCancel }) => {
  const { t } = useTranslation();
  const isApple = isAppleSubscription(subscription);
  const {
    fetch: fetchStripeUrl,
    url: stripeUrl,
    isLoading,
  } = useOpenSubscriptionManagement(subscription.stripeCustomerId);

  const manageLabel = t(
    'dialogs.delete-account.subscription-warning.button.manage',
  );
  const cancelLabel = t(
    'dialogs.delete-account.subscription-warning.button.cancel',
  );

  const manageUrl = isApple ? subscription.managementUrl : stripeUrl;
  // Without a management url, only a Stripe customer has a portal to open; any other subscription gets no dead button.
  const canFetchStripeUrl = !isApple && !!subscription.stripeCustomerId;

  return (
    <>
      <Styled.Header>
        <Typography variant="h5" component="h1">
          {t('dialogs.delete-account.subscription-warning.title')}
        </Typography>
      </Styled.Header>

      <Styled.Body>
        <Typography variant="body2">
          {t('dialogs.delete-account.subscription-warning.description')}
        </Typography>
      </Styled.Body>

      <Styled.Interactions>
        <Button
          variant={Variants.Outlined}
          size={Sizes.Medium}
          color={Colors.Inherit}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
        {manageUrl ? (
          <Button
            as="a"
            // @ts-expect-error -- Button polymorphic `href` prop typing
            href={manageUrl}
            target="_blank"
            size={Sizes.Medium}
            color={Colors.Error}
            onClick={onCancel}
          >
            {manageLabel}
          </Button>
        ) : (
          canFetchStripeUrl && (
            <Button
              size={Sizes.Medium}
              color={Colors.Error}
              onClick={fetchStripeUrl}
              disabled={isLoading}
            >
              {manageLabel}
            </Button>
          )
        )}
      </Styled.Interactions>
    </>
  );
};
